import { LitElement, html } from 'lit';
import '../quote/quote-header.js';
import '../quote/quote-class-section.js';
import '../quote/quote-financial-summary.js';
import '../icons/success-icon.js';
import './po-reference-card.js';
import './document-download-card.js';
import './payment-credit-card.js';
import './payment-bank-transfer.js';
import './payment-installments.js';

class QuotePendingPayment extends LitElement {
    static properties = {
        quoteReference: { type: String },
        quoteData: { type: Object },
    };

    createRenderRoot() {
        return this; // Light DOM for Tailwind compatibility
    }

    constructor() {
        super();
        this.quoteData = null;
    }

    get serviceClasses() {
        return this.quoteData?.class || [];
    }

    get totalAmount() {
        return this.quoteData?.total || '$0.00';
    }

    get meetsInstallmentMinimum() {
        const minimum = parseFloat(import.meta.env.VITE_PROCURET_MINIMUM_AMOUNT);
        const amount = parseFloat(this.totalAmount.replace(/[$,]/g, ''));
        return amount >= minimum;
    }

    get minimumAmountFormatted() {
        const minimum = parseFloat(import.meta.env.VITE_PROCURET_MINIMUM_AMOUNT);
        return (
            '$' +
            minimum.toLocaleString('en-NZ', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        );
    }

    render() {
        if (!this.quoteData) {
            return html`<div></div>`;
        }

        return html`
            <div class="space-y-6 py-6">
                <!-- Acceptance Confirmation -->
                <div class="alert alert-row alert-success">
                    <success-icon class="shrink-0"></success-icon>
                    <div class="text-left">
                        <h3 class="font-bold">Quote Accepted!</h3>
                        <p class="text-sm">
                            Thank you for accepting quote
                            <strong>${this.quoteReference}</strong>. Please complete payment to
                            confirm your booking.
                        </p>
                        <p class="text-sm mt-2">
                            <strong>Note:</strong> If you have recently made a payment, please allow
                            some time for the payment status to be reflected on this page.
                        </p>
                    </div>
                </div>

                <!-- PO Reference -->
                <po-reference-card
                    quoteReference="${this.quoteReference}"
                    custRef="${this.quoteData?.custRef || ''}"
                ></po-reference-card>

                <!-- Download Invoice -->
                <document-download-card
                    quoteReference="${this.quoteReference}"
                    quoteId="${this.quoteReference}"
                    custRef="${this.quoteData?.custRef || ''}"
                    cardTitle="Invoice"
                    cardDescription="Download your invoice for campaign ${this
                        .quoteReference} for your reference."
                    cardNote="Please note: it may take a few minutes for your invoice to be generated. If it's not available yet, please try again shortly."
                    buttonText="Download Invoice"
                    documentType="invoice"
                ></document-download-card>

                <!-- Payment Options -->
                <div class="mb-8">
                    <h2 class="text-3xl font-bold mb-2">Payment Options</h2>
                    <p class="text-gray-600">
                        Choose your preferred payment method to complete your quote acceptance.
                    </p>
                </div>

                <payment-credit-card
                    quoteReference="${this.quoteReference}"
                    totalAmount="${this.totalAmount}"
                    companyName="${this.quoteData?.custName || ''}"
                    contactName="${this.quoteData?.contactName || ''}"
                ></payment-credit-card>

                <payment-bank-transfer
                    quoteReference="${this.quoteReference}"
                    totalAmount="${this.totalAmount}"
                    bankAccount="${this.quoteData?.bankAcct || 'N/A'}"
                    bankAddress="${this.quoteData?.bankAddress || 'N/A'}"
                ></payment-bank-transfer>

                <payment-installments
                    quoteReference="${this.quoteReference}"
                    totalAmount="${this.totalAmount}"
                    contactEmail="${this.quoteData?.contactEmail || ''}"
                    ?meetsMinimum="${this.meetsInstallmentMinimum}"
                    minimumFormatted="${this.minimumAmountFormatted}"
                ></payment-installments>

                <!-- Quote Summary -->
                <div class="mb-4">
                    <h3 class="text-xl font-bold mb-4">Quote Summary</h3>
                    <quote-header .quoteData="${this.quoteData}"></quote-header>
                </div>

                <!-- Service Classes -->
                <div class="space-y-6 mt-8">
                    ${this.serviceClasses.length > 0
                        ? this.serviceClasses.map(
                              (classData) => html`
                                  <quote-class-section
                                      .classData="${classData}"
                                      .sputnikQuoteId="${this.quoteData?.sputnikQuoteId}"
                                  ></quote-class-section>
                              `
                          )
                        : html`
                              <div class="alert alert-row alert-warning">
                                  <warning-icon class="shrink-0"></warning-icon>
                                  <span class="text-left"
                                      >No service classes found in this quote.</span
                                  >
                              </div>
                          `}
                </div>

                <quote-financial-summary .quoteData="${this.quoteData}"></quote-financial-summary>
            </div>
        `;
    }
}

customElements.define('quote-pending-payment', QuotePendingPayment);
